/**
 * ProtectedRoute.
 *
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to /login.
 *
 * Usage in App.tsx:
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/dashboard" element={<DashboardPage />} />
 *   </Route>
 */

import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { setCanonicalLink, setOpenGraphUrl, setRobotsMeta, setStructuredDataScripts } from '../../lib/page-meta';

export function ProtectedRoute() {
  const { isAuthenticated, isBootstrapping, hasPendingVerification, hasPendingWorkspaceSelection } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const canonicalUrl = new URL(location.pathname, window.location.origin).toString();
    const applyPrivateSeo = () => {
      setRobotsMeta('noindex,nofollow');
      setCanonicalLink(canonicalUrl);
      setOpenGraphUrl(canonicalUrl);
      setStructuredDataScripts([]);
    };

    applyPrivateSeo();
    const frame = window.requestAnimationFrame(applyPrivateSeo);
    return () => window.cancelAnimationFrame(frame);
  }, [isAuthenticated, location.pathname]);

  if (isBootstrapping) {
    return null;
  }

  if (!isAuthenticated) {
    if (hasPendingVerification) {
      return <Navigate to="/verify-email" replace />;
    }

    if (hasPendingWorkspaceSelection) {
      return <Navigate to="/select-workspace" replace />;
    }

    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
